import { intlShape } from 'react-intl';
import autobind from 'core-decorators/lib/autobind';
import classnames from 'classnames';
import clamp from 'text-overflow-clamp';

import { WixConnect } from 'widget/player-widget/utils/wix-connect';
import resizeImage from 'widget/player-widget/utils/resize-image';

import { pickContextTypes } from '../../context-types';

import ScrollbarWrapper from 'widget/player-widget/components/scrollbar-wrapper/scrollbar-wrapper';

import styles from './info.styl';

import translations from './translations';
import crewTranslations from 'shared/translations/crew';
import genreTranslations from 'shared/translations/genres';


const POSTER_SIZE = {
  width: 303,
  height: 449,
};

const POSTER_SIZE_FULLSCREEN = {
  width: 450,
  height: 667,
};


@WixConnect(styles)
export default class InfoOverlayInfo extends React.Component {

  static propTypes = {
    appSettings: React.PropTypes.object,
    intl: intlShape,
  };

  static contextTypes = pickContextTypes('videoItem', 'fullScreen');

  componentDidMount() {
    this.clamp();

    window.addEventListener('resize', this.setPosterSize);
    this.setPosterSize();
  }

  componentDidUpdate() {
    this.clamp();
    this.setPosterSize();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setPosterSize);
  }

  title = null;
  info = null;
  posterEl = null;

  @autobind
  savePosterRef(node) {
    this.posterEl = node;
  }

  @autobind
  saveTitleRef(node) {
    this.title = node;
  }

  clamp() {
    clamp(this.title, { lineClamp: 3 });
  }

  get formatMessage() {
    return this.props.intl.formatMessage;
  }

  getRoles(rolesType) {
    const { formatMessage } = this;
    const { videoItem: { [rolesType]: roles } } = this.context;

    if (!roles || !roles.length) {
      return null;
    }

    const roleElements = _.map(roles, function ({ role, name }, index) {
      if (rolesType === 'crew') {
        role = formatMessage(crewTranslations[role]);
      }

      return (
        <div key={index}
             styleName="role-item"
             className={`qa-overlay-info-${rolesType}`}>
          <div styleName="role">{role}</div>
          <div styleName="name">{name}</div>
        </div>
      );
    });

    return (
      <div styleName="roles-wrapper">
        <div styleName="roles-title">
          {formatMessage(translations[rolesType])}
        </div>

        {roleElements}
      </div>
    );
  }

  @autobind
  setPosterSize() {
    const { fullScreen } = this.context;
    this.updatePosterStyle(fullScreen ? POSTER_SIZE_FULLSCREEN : POSTER_SIZE);
  }

  updatePosterStyle({ width, height }) {
    const poster = this.posterEl;

    if (!poster) {
      return null;
    }

    poster.style.width = `${Math.floor(poster.offsetHeight / height * width)}px`;
  }

  getPosterImage(width, height, className) {
    const { videoItem: { posterUrl }, fullScreen } = this.context;
    const { hosts } = this.props.appSettings;

    if (!posterUrl) {
      return null;
    }

    const resizedPosterUrl = resizeImage(posterUrl, width * 2, height * 2);

    return (
      <div ref={this.savePosterRef}
           styleName={classnames('poster', className, { fullscreen: fullScreen })}
           className="qa-overlay-info-poster"
           style={{ backgroundImage: `url(${hosts.image}${resizedPosterUrl})` }} />
    );
  }

  get compactPoster() {
    const { appSettings } = this.props;
    const { videoItem } = this.context;
    const { posterUrl } = videoItem;

    if (!posterUrl) {
      return null;
    }

    const resizedPosterUrl = resizeImage(posterUrl, POSTER_SIZE.width * 2, POSTER_SIZE.height * 2);

    const { hosts } = appSettings;
    const { title } = videoItem;

    return (
      <img styleName="poster poster-compact"
           className="qa-overlay-info-poster-compact"
           src={`${hosts.image}${resizedPosterUrl}`}
           alt={title} />
    );
  }

  get poster() {
    const { fullScreen } = this.context;

    if (fullScreen) {
      return null;
    }

    return this.getPosterImage(POSTER_SIZE.width, POSTER_SIZE.height);
  }

  get fullScreenPoster() {
    const { fullScreen } = this.context;

    if (!fullScreen) {
      return null;
    }

    return this.getPosterImage(POSTER_SIZE_FULLSCREEN.width, POSTER_SIZE_FULLSCREEN.height);
  }

  get publisher() {
    const { videoItem: { publisher } } = this.context;
    const { formatMessage } = this;

    if (!publisher) {
      return null;
    }

    return (
      <div styleName="info-item"
           className="qa-overlay-info-publisher">
        {formatMessage(translations.publisher, { author: publisher })}
      </div>
    );
  }

  get genres() {
    const { videoItem: { genre } } = this.context;
    const { formatMessage } = this;

    if (!genre) {
      return null;
    }

    return (
      <div styleName="info-item"
           className="qa-overlay-info-genres">
        {formatMessage(genreTranslations[genre])}
      </div>
    );
  }

  get duration() {
    const { videoItem: { durationStr } } = this.context;

    if (!durationStr) {
      return null;
    }

    return (
      <div styleName="info-item"
           className="qa-overlay-info-duration">
        {` ${durationStr}`}
      </div>
    );
  }

  get description() {
    const { videoItem: { description } } = this.context;

    if (!description) {
      return null;
    }

    return (
      <div styleName="description"
           className="qa-overlay-info-description">
        {description}
      </div>
    );
  }

  render() {
    const { videoItem: { title }, fullScreen } = this.context;

    const styleNames = classnames('wrapper', {
      'full-screen': fullScreen,
    });

    return (
      <div styleName={styleNames}
           className="qa-player-overlay-info">
        {this.poster}

        <ScrollbarWrapper styleName="scrollbar-wrapper">
          <div styleName="main-info">
            <div ref={this.saveTitleRef}
                 styleName="title"
                 className="qa-overlay-info-title"
                 title={title}>
              {title}
            </div>

            <div styleName="info">
              {this.publisher}
              {this.genres}
              {this.duration}
            </div>

            {this.description}
            {this.fullScreenPoster}

            {this.getRoles('cast')}
            {this.getRoles('crew')}
            {this.compactPoster}
          </div>
        </ScrollbarWrapper>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/player-widget/components/player/info-overlay/info/info.js